// Reusable External Destination Viewer — used across YulOnTap (restaurants,
// menus, entertainment, tourism, attractions…). Tiered behaviour:
// embed when the site permits it, otherwise a contained fallback that opens
// the official site in a new tab while the YulOnTap session stays alive.
// Never bypasses X-Frame-Options / CSP — the backend only reads headers.
import { useState, useEffect } from 'react';
import { ChevronLeft, ExternalLink, Globe, Loader2 } from 'lucide-react';

export default function ExternalDestinationViewer({ url, title, tx, onClose }) {
    const [mode, setMode] = useState('checking');
    const [opened, setOpened] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setMode('checking');
        fetch(`/api/assistant/embed-check?url=${encodeURIComponent(url)}`)
            .then((r) => (r.ok ? r.json() : { embeddable: false }))
            .then((d) => { if (!cancelled) setMode(d.embeddable ? 'embed' : 'external'); })
            .catch(() => { if (!cancelled) setMode('external'); });
        return () => { cancelled = true; };
    }, [url]);

    const openExternal = () => {
        window.open(url, '_blank', 'noopener');
        setOpened(true);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col" data-testid="external-destination-viewer">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-900 border-b border-white/10 shrink-0">
                <button type="button" onClick={onClose}
                    className="inline-flex items-center gap-0.5 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors shrink-0"
                    data-testid="destination-back-btn">
                    <ChevronLeft className="w-5 h-5" /> {tx.backToApp}
                </button>
                <p className="flex-1 text-white text-sm font-medium truncate text-center min-w-0">{title}</p>
                <button type="button" onClick={openExternal} title={tx.opensNewTab}
                    className="p-1.5 text-slate-400 hover:text-white transition-colors shrink-0"
                    data-testid="destination-open-tab-btn">
                    <ExternalLink className="w-4 h-4" />
                </button>
            </div>

            {mode === 'checking' && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3" data-testid="destination-checking">
                    <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
                    <p className="text-slate-400 text-sm">{tx.destChecking}</p>
                </div>
            )}

            {mode === 'embed' && (
                <iframe src={url} title={title || 'External site'}
                    className="flex-1 w-full bg-white border-0"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="destination-iframe" />
            )}

            {mode === 'external' && (
                <div className="flex-1 flex items-center justify-center p-6" data-testid="destination-external">
                    <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                        <Globe className="w-10 h-10 text-emerald-400 mx-auto" />
                        <p className="text-white font-semibold mt-3">{title}</p>
                        <p className="text-slate-300 text-sm mt-2">{tx.destExternalNote}</p>
                        <button type="button" onClick={openExternal}
                            className="mt-4 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                            data-testid="destination-open-btn">
                            {tx.destOpenSite}
                        </button>
                        {opened && (
                            <p className="text-emerald-300 text-xs mt-3" data-testid="destination-opened-note">{tx.destOpened}</p>
                        )}
                        <button type="button" onClick={onClose}
                            className="mt-3 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                            data-testid="destination-close-btn">
                            {tx.back}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
